import { EVENT_TITLES, WEB_LIBRARY_ROUTES } from "@/constants/libraries";
import { authenticate, authenticateAdmin, LOCAL_ACCESS_KEY } from "@/services";
import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { RESET_PROJECT_STORE_ACTION } from "../store/constants.js";
import { store } from "../store/index.js";

// Public-facing pages
import Demo from "../views/public/Demo.vue";
import Health from "../views/public/Health.vue";
import Home from "../views/public/Home.vue";
import PrivacyPolicy from "../views/public/legal/PrivacyPolicy.vue";

//Error pages
import InternalServerError from "../views/error/InternalServerError.vue";
import ResourceNotFound from "../views/error/ResourceNotFound.vue";

//WebLibrary
import LikedInfographics from "@/views/WebLibrary/components/LikedInfographics.vue";
import AboutPage from "../views/WebLibrary/AboutPage.vue";
import AgendaPage from "../views/WebLibrary/AgendaPage.vue";
import DayPage from "../views/WebLibrary/DayPage.vue";
import WebLibrary from "../views/WebLibrary/Home.vue";
import SearchResultsPage from "../views/WebLibrary/SearchResultsPage.vue";
import StagePage from "../views/WebLibrary/StagePage.vue";
import TalkPage from "../views/WebLibrary/TalkPage.vue";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "Home",
    component: Home,
  },
  {
    path: "/mainHome",
    name: "MainHome",
    redirect: "",
    beforeEnter() {
      window.location.href = "https://thoth.art";
    },
  },
  {
    path: "/demo",
    name: "Demo",
    component: Demo,
  },
  {
    path: "/app",
    name: "App",
    redirect: "",
    beforeEnter() {
      window.location.href = "https://app.thoth.art";
    },
  },
  {
    path: "/404",
    name: "ResourceNotFound",
    component: ResourceNotFound,
  },
  {
    path: "/500",
    name: "InternalServerError",
    component: InternalServerError,
  },
  {
    path: "/health",
    name: "Health",
    component: Health,
  },
  {
    path: "/:eventName",
    name: "WebLibraryHome",
    component: WebLibrary,
    props: true,
  },
  {
    path: "/:eventName/stage/:name",
    name: "StagePage",
    component: StagePage,
    props: true,
  },
  {
    path: "/:eventName/talk/:name",
    name: "TalkPage",
    component: TalkPage,
    props: true,
  },
  {
    path: "/:eventName/day/:day",
    name: "DayPage",
    component: DayPage,
    props: true,
  },
  {
    path: "/:eventName/search",
    name: "SearchResultsPage",
    component: SearchResultsPage,
    props: true,
  },
  {
    path: "/:eventName/agenda",
    name: "AgendaPage",
    props: true,
    component: AgendaPage,
  },
  {
    path: "/:eventName/about",
    name: "AboutPage",
    component: AboutPage,
    props: true,
  },
  {
    path: "/:eventName/likeinfographics",
    name: "LikeInfographics",
    component: LikedInfographics,
    props: true,
  },
  {
    path: "/:catchAll(.*)", // Catch all routes that don't match any of the defined routes
    name: "CatchAll",
    redirect: "/404",
  },
  {
    path: "/privacy",
    name: "Privacy",
    component: PrivacyPolicy,
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior() {
    document.getElementById("app")?.scrollIntoView();
  },
});

const PRODUCT_FLOW_ROUTES = ["Create", "Preview", "Edit", "Publish"];
const PROTECTED_ROUTES = [
  ...PRODUCT_FLOW_ROUTES,
  "Dashboard",
  "BrandKit",
  "Settings",
];

router.beforeEach(async (to, from, next) => {
  // Handle case-insensitive routing for WebLibrary routes BEFORE S3 config fetch
  // This prevents 404s when S3 URLs are case-sensitive

  if (to.name && WEB_LIBRARY_ROUTES.includes(to.name as string)) {
    const eventName = to.params.eventName as string;
    if (eventName && eventName !== eventName.toLowerCase()) {
      // Redirect to lowercase version to match S3 structure
      return next({
        ...to,
        params: {
          ...to.params,
          eventName: eventName.toLowerCase(),
        },
      });
    }
  }

  const token = localStorage.getItem(LOCAL_ACCESS_KEY);
  const isAuthenticated = token ? await authenticate() : false;

  if (to.name === "Admin") {
    const isAdmin = token ? await authenticateAdmin() : false;
    if (isAdmin && isAuthenticated) {
      next();
    } else {
      next({ name: "Login" });
    }
  } else {
    if (isAuthenticated || from.name == "Login") {
      next();
    } else if (PROTECTED_ROUTES.includes(String(to.name)) && !isAuthenticated) {
      next({ name: "Login" });
    } else if (to.name === "Success") {
      next({ name: "Pricing" });
    } else {
      next();
    }
  }
});
router.afterEach(async (to, from) => {
  if (
    PRODUCT_FLOW_ROUTES.includes(String(from.name)) &&
    to.name === "Dashboard"
  ) {
    store.dispatch(RESET_PROJECT_STORE_ACTION);
  }

  // Set dynamic titles for WebLibrary routes
  if (
    to.name &&
    WEB_LIBRARY_ROUTES.includes(to.name as string) &&
    to.params.eventName
  ) {
    const eventName = to.params.eventName as string;
    document.title = `Thoth.ai | ${EVENT_TITLES[eventName] ||
      eventName.replace(/(\d{2})(\d{4})/, "$1 $2")}`;
  }
});

export default router;
